import Reveal3 from '@/components/marketing3/Reveal3'
import { cn } from '@/lib/utils'

/**
 * Founder3 — light, warm "who's behind it" story + the pull-quote. Adapts Landing2's
 * OperatorsSection pull-quote treatment (a teal-ruled blockquote) to daylight mode.
 *
 * Every string is VERBATIM from the copy doc's §Who's behind it — heading, story, and
 * pull-quote. The competitor mention is excised ("the tools everyone else uses",
 * §0 competitor-name ban); no cadence, no PMS name. {BRAND} slots via the `brand` prop.
 */

const container = 'mx-auto max-w-6xl px-4 sm:px-6'

export default function Founder3({ brand }: { brand: string }) {
  return (
    <section id="who" aria-label={`Who’s behind ${brand}`} className="border-t border-stone-200/70">
      <div className={cn(container, 'py-24 sm:py-28')}>
        <Reveal3 className="max-w-3xl">
          <p className="mkt3-eyebrow">Who’s behind it</p>
          <h2 className="mkt3-display mkt3-h2 mt-4">Built downstairs, in real hotels.</h2>
        </Reveal3>

        <div className="mt-10 grid gap-12 lg:grid-cols-[1.4fr_1fr] lg:gap-16">
          <Reveal3 delay={100}>
            <div className="space-y-5">
              <p className="mkt3-prose">
                We’re hoteliers first. For years we priced our own boutique properties the way most independents do —
                with the tools everyone else uses, and a nagging feeling that we couldn’t see why any given number was
                what it was.
              </p>
              <p className="mkt3-prose">
                So we built the engine we wanted: deterministic, explainable, ours. Then we ran the only test that
                matters — we moved our own hotels onto it and let it set the prices our own revenue depends on. It
                still does, tonight included.
              </p>
              <p className="mkt3-prose">
                {brand} is that engine, opened up to other hoteliers. We don’t sell anything we don’t run ourselves.
              </p>
            </div>
          </Reveal3>

          <Reveal3 delay={220} className="flex items-center">
            <blockquote className="border-l-2 border-teal-500/50 pl-6">
              <p className="mkt3-display mkt3-quote text-teal-700">“It prices our rooms tonight.”</p>
              <footer className="mkt3-label mt-4 normal-case tracking-[0.18em] text-slate-500">
                The whole pitch, in five words.
              </footer>
            </blockquote>
          </Reveal3>
        </div>
      </div>
    </section>
  )
}
